import React from 'react';
import {
  ActionButton,
  Text,
  MenuTrigger,
  Menu,
  Item
} from '@adobe/react-spectrum';
import { useNavigate } from 'react-router-dom';
import { HelpIcon } from '../HelpIcon';
import styles from './HelpMenu.module.scss';
import { HELP_LINKS } from './helpConstants';

interface HelpMenuOption {
  key: string;
  label: string;
  action: () => void;
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const HelpMenu: React.FC = () => {
  const navigate = useNavigate();
  const helpOptions: HelpMenuOption[] = [
    {
      key: 'tutorials',
      label: 'User Guide',
      action: () => navigate('/user-guide')
    },
    {
      key: 'whats-new',
      label: "What's New",
      action: () => navigate('/user-guide?section=changelog')
    },
    {
      key: 'support',
      label: 'Request Support',
      action: () => {
        // ✅ Open Outlook Web App directly - ONLY way to explicitly force Outlook
        // Research shows: No desktop protocol works reliably to force Outlook specifically
        // - mailto: opens default mail client (might not be Outlook)
        // - ms-outlook:// causes "unknown host" errors
        // - Outlook Web is the ONLY guaranteed way to use Outlook
        const email = HELP_LINKS.SUPPORT_EMAIL;
        const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?to=${email}`;
        openInNewTab(outlookWebUrl);
      }
    },
    {
      key: 'feedback',
      label: 'Provide Feedback',
      action: () => openInNewTab(HELP_LINKS.FEEDBACK_FORM)
    }
  ];

  const handleMenuAction = (key: React.Key) => {
    const option = helpOptions.find(opt => opt.key === key);
    if (option) {
      option.action();
    }
  };

  return (
    <MenuTrigger align="end">
      <ActionButton
        data-testid="roadmap-header-help-button"
        aria-label="Help"
        UNSAFE_className={styles.helpToggle}
      >
        <HelpIcon />
        <Text UNSAFE_style={{ fontWeight: '700' }}>Help</Text>
      </ActionButton>
      <Menu
        onAction={handleMenuAction}
        data-testid="help-menu"
        UNSAFE_className={styles.helpMenu}
      >
        {helpOptions.map(option => (
          <Item key={option.key} data-testid={`help-menu-${option.key}`}>
            {option.label}
          </Item>
        ))}
      </Menu>
    </MenuTrigger>
  );
};
